/**
 * TemplatesAdminPage - Comprehensive admin for all pilot templates
 * Manages: Instructor pilots, Event pilots, Service provider templates
 * Features: CRUD, live/draft status, intake field editing, defaults sync
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Settings,
  Users,
  Calendar,
  Sparkles,
  Moon,
  Rocket,
  Scissors,
  Building,
  Save,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Edit2,
  ChevronRight,
  Check,
  X,
  GripVertical,
  RefreshCw,
  Loader2,
  AlertCircle,
  FileText,
  Layers,
  Target,
  Heart,
  Accessibility,
  Utensils,
  Music,
  Globe
} from "lucide-react";

// Template type configurations
const TEMPLATE_TYPES = {
  instructor: {
    label: "Instructor Pilot",
    icon: Sparkles,
    color: "violet",
    description: "For workshops, learning sessions, and educational events"
  },
  irl_event: {
    label: "IRL Event Pilot",
    icon: Calendar,
    color: "emerald",
    description: "For in-person events with venue, dietary, and accessibility needs"
  },
  url_event: {
    label: "URL Event Pilot",
    icon: Globe,
    color: "blue",
    description: "For virtual/online events with recording and streaming features"
  },
  service_provider: {
    label: "Service Provider",
    icon: Scissors,
    color: "pink",
    description: "For beauty, wellness, and service-based professionals"
  }
};

// Field category icons
const FIELD_ICONS = {
  audience_types: Users,
  interests: Heart,
  builder_types: Target,
  industries: Building,
  topics_of_interest: FileText,
  accessibility_options: Accessibility,
  dietary_options: Utensils,
  vibe_options: Music
};

export default function TemplatesAdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("instructor");
  
  // All templates and profiles
  const [templates, setTemplates] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  
  // Selected item for editing
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Bulk edit state
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState(new Set());
  const [bulkEditData, setBulkEditData] = useState(null);
  const [showBulkApplyDialog, setShowBulkApplyDialog] = useState(false);
  
  // Edit state
  const [editData, setEditData] = useState(null);
  const [newFieldDialog, setNewFieldDialog] = useState({ open: false, category: null });
  const [newFieldName, setNewFieldName] = useState("");
  
  useEffect(() => {
    fetchAllData();
  }, []);
  
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // Fetch templates
      const templatesRes = await fetch(`${API}/instructor-pilots/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
      
      // Fetch profiles (instances)
      const profilesRes = await fetch(`${API}/instructor-pilots/profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (profilesRes.ok) {
        const data = await profilesRes.json();
        setProfiles(data.profiles || []);
      }
      
      // Fetch organizations
      const orgsRes = await fetch(`${API}/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (orgsRes.ok) {
        const data = await orgsRes.json();
        setOrganizations(data.organizations || []);
      }
      
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };
  
  const selectProfile = (profile) => {
    setSelectedProfile(profile);
    setEditData(JSON.parse(JSON.stringify(profile))); // Deep clone
    setEditMode(false);
  };
  
  const handleSaveProfile = async () => {
    if (!editData) return;
    setSaving(true);
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/instructor-pilots/profiles/${editData.profile_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("Profile saved!");
        
        // Update local state
        setProfiles(prev => prev.map(p => 
          p.profile_id === editData.profile_id ? data.profile : p
        ));
        setSelectedProfile(data.profile);
        setEditMode(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };
  
  const toggleFieldEnabled = (category, fieldId) => {
    if (!editData) return;
    
    setEditData(prev => ({
      ...prev,
      [category]: (prev[category] || []).map(f => 
        f.id === fieldId ? { ...f, enabled: !f.enabled } : f
      )
    }));
  };
  
  const addNewField = (category) => {
    if (!newFieldName.trim()) return;
    
    const newField = {
      id: newFieldName.toLowerCase().replace(/\s+/g, '_'),
      label: newFieldName,
      enabled: true
    };
    
    setEditData(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), newField]
    }));
    
    setNewFieldName("");
    setNewFieldDialog({ open: false, category: null });
    toast.success(`Added "${newFieldName}" to ${category.replace(/_/g, ' ')}`);
  };
  
  const removeField = (category, fieldId) => {
    setEditData(prev => ({
      ...prev,
      [category]: (prev[category] || []).filter(f => f.id !== fieldId)
    }));
  };
  
  const updateFieldLabel = (category, fieldId, newLabel) => {
    setEditData(prev => ({
      ...prev,
      [category]: (prev[category] || []).map(f =>
        f.id === fieldId ? { ...f, label: newLabel } : f
      )
    }));
  };
  
  // ==================== BULK EDIT FUNCTIONS ====================
  
  const toggleBulkEditMode = () => {
    setBulkEditMode(!bulkEditMode);
    setSelectedProfiles(new Set());
    setBulkEditData(null);
  };
  
  const toggleProfileSelection = (profileId) => {
    setSelectedProfiles(prev => {
      const next = new Set(prev);
      if (next.has(profileId)) {
        next.delete(profileId);
      } else {
        next.add(profileId);
      }
      return next;
    });
  };
  
  const selectAllProfiles = () => {
    const allIds = filteredProfiles.map(p => p.profile_id);
    setSelectedProfiles(new Set(allIds));
  };
  
  const deselectAllProfiles = () => {
    setSelectedProfiles(new Set());
  };
  
  const initBulkEdit = () => {
    // Use first selected profile as template for bulk edit
    const firstSelectedId = Array.from(selectedProfiles)[0];
    const templateProfile = profiles.find(p => p.profile_id === firstSelectedId);
    if (templateProfile) {
      setBulkEditData(JSON.parse(JSON.stringify(templateProfile)));
      setShowBulkApplyDialog(true);
    }
  };
  
  const handleBulkApply = async (fieldsToApply) => {
    if (!bulkEditData || selectedProfiles.size === 0) return;
    setSaving(true);
    
    try {
      const token = localStorage.getItem("session_token");
      const updatePromises = [];
      
      for (const profileId of selectedProfiles) {
        const currentProfile = profiles.find(p => p.profile_id === profileId);
        if (!currentProfile) continue;
        
        // Build update data based on selected fields to apply
        const updateData = { ...currentProfile };
        
        if (fieldsToApply.event_defaults) {
          updateData.event_defaults = bulkEditData.event_defaults;
        }
        if (fieldsToApply.intake_settings) {
          updateData.intake_settings = bulkEditData.intake_settings;
        }
        if (fieldsToApply.host_display_controls) {
          updateData.host_display_controls = bulkEditData.host_display_controls;
        }
        if (fieldsToApply.welcome_config) {
          updateData.welcome_config = bulkEditData.welcome_config;
        }
        if (fieldsToApply.audience_types) {
          updateData.audience_types = bulkEditData.audience_types;
        }
        if (fieldsToApply.interests) {
          updateData.interests = bulkEditData.interests;
        }
        if (fieldsToApply.builder_types) {
          updateData.builder_types = bulkEditData.builder_types;
        }
        if (fieldsToApply.industries) {
          updateData.industries = bulkEditData.industries;
        }
        if (fieldsToApply.accessibility_options) {
          updateData.accessibility_options = bulkEditData.accessibility_options;
        }
        if (fieldsToApply.dietary_options) {
          updateData.dietary_options = bulkEditData.dietary_options;
        }
        
        updatePromises.push(
          fetch(`${API}/instructor-pilots/profiles/${profileId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(updateData)
          })
        );
      }
      
      const results = await Promise.all(updatePromises);
      const successCount = results.filter(r => r.ok).length;
      
      toast.success(`Updated ${successCount} of ${selectedProfiles.size} profiles`);
      
      // Refresh data
      fetchAllData();
      setShowBulkApplyDialog(false);
      setBulkEditMode(false);
      setSelectedProfiles(new Set());
      setBulkEditData(null);
      
    } catch (err) {
      console.error("Bulk update error:", err);
      toast.error("Failed to apply bulk updates");
    } finally {
      setSaving(false);
    }
  };
  
  // Filter profiles by type based on active tab
  const getFilteredProfiles = () => {
    return profiles.filter(p => {
      if (activeTab === "instructor") return p.pilot_type === "instructor" || !p.pilot_type;
      if (activeTab === "irl_event") return p.pilot_type === "irl_event";
      if (activeTab === "url_event") return p.pilot_type === "url_event";
      if (activeTab === "service_provider") return p.pilot_type === "service_provider";
      return true;
    });
  };
  
  const filteredProfiles = getFilteredProfiles();
  const typeConfig = TEMPLATE_TYPES[activeTab];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Layers className="w-6 h-6" />
              Templates & Pilots
            </h1>
            <p className="text-gray-600">Manage all pilot templates, intake fields, and defaults</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant={bulkEditMode ? "default" : "outline"} 
              onClick={toggleBulkEditMode}
              className={bulkEditMode ? "bg-violet-600 hover:bg-violet-700" : ""}
            >
              <Layers className="w-4 h-4 mr-2" />
              {bulkEditMode ? "Exit Bulk Edit" : "Bulk Edit"}
            </Button>
            <Button variant="outline" onClick={fetchAllData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => navigate("/admin/instructor-pilots")}>
              <Plus className="w-4 h-4 mr-2" />
              Create New Pilot
            </Button>
          </div>
        </div>
        
        {/* Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            {Object.entries(TEMPLATE_TYPES).map(([key, config]) => {
              const Icon = config.icon;
              const count = profiles.filter(p => 
                key === "instructor" ? (p.pilot_type === "instructor" || !p.pilot_type) : p.pilot_type === key
              ).length;
              
              return (
                <TabsTrigger key={key} value={key} className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{config.label.split(' ')[0]}</span>
                  {count > 0 && <Badge variant="secondary" className="text-xs">{count}</Badge>}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
        
        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Profile List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <typeConfig.icon className={`w-4 h-4 text-${typeConfig.color}-600`} />
                      {typeConfig.label}s
                    </CardTitle>
                    <CardDescription className="text-xs">{typeConfig.description}</CardDescription>
                  </div>
                  
                  {/* Bulk Edit Selection Controls */}
                  {bulkEditMode && filteredProfiles.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={selectedProfiles.size === filteredProfiles.length ? deselectAllProfiles : selectAllProfiles}
                        className="text-xs"
                      >
                        {selectedProfiles.size === filteredProfiles.length ? "Deselect All" : "Select All"}
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Bulk Edit Action Bar */}
                {bulkEditMode && selectedProfiles.size > 0 && (
                  <div className="mt-3 p-2 bg-violet-50 rounded-lg border border-violet-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-violet-700 font-medium">
                        {selectedProfiles.size} selected
                      </span>
                      <Button 
                        size="sm" 
                        onClick={initBulkEdit}
                        className="bg-violet-600 hover:bg-violet-700"
                      >
                        Apply Changes
                      </Button>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  {filteredProfiles.length > 0 ? (
                    <div className="space-y-2">
                      {filteredProfiles.map(profile => (
                        <div
                          key={profile.profile_id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            bulkEditMode && selectedProfiles.has(profile.profile_id)
                              ? "border-violet-500 bg-violet-50 ring-1 ring-violet-500"
                              : selectedProfile?.profile_id === profile.profile_id
                              ? `border-${typeConfig.color}-500 bg-${typeConfig.color}-50`
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                          onClick={() => bulkEditMode ? toggleProfileSelection(profile.profile_id) : selectProfile(profile)}
                          data-testid={`profile-item-${profile.profile_id}`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Checkbox for bulk edit */}
                            {bulkEditMode && (
                              <div 
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  selectedProfiles.has(profile.profile_id)
                                    ? "bg-violet-600 border-violet-600"
                                    : "border-gray-300"
                                }`}
                              >
                                {selectedProfiles.has(profile.profile_id) && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                            )}
                            
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: profile.color || '#8b5cf6' }}
                            >
                              <typeConfig.icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{profile.name}</p>
                              <p className="text-xs text-gray-500">
                                {profile.org?.name || 'No org'} • {profile.total_events || 0} events
                              </p>
                            </div>
                            {!bulkEditMode && <ChevronRight className="w-4 h-4 text-gray-400" />}
                          </div>
                          
                          <div className="flex gap-1 mt-2">
                            <Badge 
                              variant="outline" 
                              className={`text-[10px] ${profile.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100'}`}
                            >
                              {profile.status || 'draft'}
                            </Badge>
                            {profile.is_public && (
                              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">
                                Public
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <typeConfig.icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No {typeConfig.label.toLowerCase()}s yet</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3"
                        onClick={() => navigate("/admin/instructor-pilots")}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Create
                      </Button>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
          
          {/* Right: Profile Editor */}
          <div className="lg:col-span-2">
            {selectedProfile && editData ? (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: editData.color || '#8b5cf6' }}
                      >
                        <typeConfig.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{editData.name}</CardTitle>
                        <CardDescription>{editData.org?.name || 'No organization'}</CardDescription>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {editMode ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => {
                            setEditData(JSON.parse(JSON.stringify(selectedProfile)));
                            setEditMode(false);
                          }}>
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveProfile} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                            Save
                          </Button>
                        </>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <Tabs defaultValue="fields">
                    <TabsList className="mb-4 flex-wrap">
                      <TabsTrigger value="fields">Intake Fields</TabsTrigger>
                      <TabsTrigger value="intake">Intake Settings</TabsTrigger>
                      <TabsTrigger value="defaults">Defaults</TabsTrigger>
                      <TabsTrigger value="display">Host Controls</TabsTrigger>
                      <TabsTrigger value="welcome">Welcome Page</TabsTrigger>
                      <TabsTrigger value="branding">Branding</TabsTrigger>
                      <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
                    </TabsList>
                    
                    {/* Intake Fields Tab */}
                    <TabsContent value="fields">
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-6">
                          {/* Render each field category */}
                          {['audience_types', 'interests', 'builder_types', 'industries', 'topics_of_interest', 'accessibility_options', 'dietary_options', 'vibe_options', 'builder_stage_options', 'industry_options'].map(category => {
                            const fields = editData[category];
                            if (!fields || fields.length === 0) return null;
                            
                            const CategoryIcon = FIELD_ICONS[category] || FileText;
                            const categoryLabel = category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                            
                            return (
                              <div key={category} className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <CategoryIcon className="w-4 h-4 text-gray-600" />
                                    <h4 className="font-medium text-sm">{categoryLabel}</h4>
                                    <Badge variant="secondary" className="text-xs">
                                      {fields.filter(f => f.enabled).length}/{fields.length}
                                    </Badge>
                                  </div>
                                  
                                  {editMode && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => setNewFieldDialog({ open: true, category })}
                                    >
                                      <Plus className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                                
                                <div className="flex flex-wrap gap-2">
                                  {fields.map(field => (
                                    <div
                                      key={field.id}
                                      className={`group flex items-center gap-1 px-2 py-1 rounded-md text-sm ${
                                        field.enabled 
                                          ? 'bg-violet-100 text-violet-800' 
                                          : 'bg-gray-100 text-gray-500'
                                      }`}
                                    >
                                      {editMode ? (
                                        <>
                                          <button
                                            onClick={() => toggleFieldEnabled(category, field.id)}
                                            className="hover:opacity-70"
                                          >
                                            {field.enabled ? (
                                              <Check className="w-3 h-3" />
                                            ) : (
                                              <X className="w-3 h-3" />
                                            )}
                                          </button>
                                          <input
                                            type="text"
                                            value={field.label}
                                            onChange={(e) => updateFieldLabel(category, field.id, e.target.value)}
                                            className="bg-transparent border-none outline-none w-auto text-sm"
                                            style={{ width: `${field.label.length + 1}ch` }}
                                          />
                                          <button
                                            onClick={() => removeField(category, field.id)}
                                            className="opacity-0 group-hover:opacity-100 hover:text-red-600"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </>
                                      ) : (
                                        <span>{field.label}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* Intake Settings Tab */}
                    <TabsContent value="intake">
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          <h4 className="font-medium text-sm text-gray-700">Data Collection Settings</h4>
                          
                          <div className="grid grid-cols-2 gap-3">
                            {/* Collect Dietary */}
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <Label className="text-sm">Collect Dietary Info</Label>
                                <p className="text-xs text-gray-500">Ask about dietary restrictions</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.event_defaults?.collect_dietary ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    event_defaults: { ...(prev.event_defaults || {}), collect_dietary: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.event_defaults?.collect_dietary ? "default" : "outline"}>
                                  {editData.event_defaults?.collect_dietary ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Collect Accessibility */}
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <Label className="text-sm">Collect Accessibility</Label>
                                <p className="text-xs text-gray-500">Ask about accessibility needs</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.event_defaults?.collect_accessibility ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    event_defaults: { ...(prev.event_defaults || {}), collect_accessibility: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.event_defaults?.collect_accessibility ? "default" : "outline"}>
                                  {editData.event_defaults?.collect_accessibility ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Collect Interests */}
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <Label className="text-sm">Collect Interests</Label>
                                <p className="text-xs text-gray-500">Ask about topic interests</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.event_defaults?.collect_interests ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    event_defaults: { ...(prev.event_defaults || {}), collect_interests: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.event_defaults?.collect_interests ? "default" : "outline"}>
                                  {editData.event_defaults?.collect_interests ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Collect Audience Type */}
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <Label className="text-sm">Collect Audience Type</Label>
                                <p className="text-xs text-gray-500">Ask what kind of builder/attendee</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.event_defaults?.collect_audience_type ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    event_defaults: { ...(prev.event_defaults || {}), collect_audience_type: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.event_defaults?.collect_audience_type ? "default" : "outline"}>
                                  {editData.event_defaults?.collect_audience_type ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Pre-invite Mode */}
                            <div className="flex items-center justify-between p-3 border rounded-lg col-span-2 bg-violet-50">
                              <div>
                                <Label className="text-sm">Pre-Invite Mode Default</Label>
                                <p className="text-xs text-gray-500">Events start in pre-invite mode (RSVP paused)</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.event_defaults?.pre_invite_enabled ?? false}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    event_defaults: { ...(prev.event_defaults || {}), pre_invite_enabled: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.event_defaults?.pre_invite_enabled ? "default" : "outline"}>
                                  {editData.event_defaults?.pre_invite_enabled ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Default Event Type */}
                          <div className="border rounded-lg p-4 mt-4">
                            <Label className="text-sm mb-2 block">Default Event Type</Label>
                            {editMode ? (
                              <Select
                                value={editData.default_event_type || "hybrid"}
                                onValueChange={(v) => setEditData(prev => ({ ...prev, default_event_type: v }))}
                              >
                                <SelectTrigger className="w-48">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="irl">IRL (In-Person)</SelectItem>
                                  <SelectItem value="url">URL (Virtual)</SelectItem>
                                  <SelectItem value="hybrid">Hybrid</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge variant="outline">
                                {editData.default_event_type === 'irl' ? 'In-Person' : 
                                 editData.default_event_type === 'url' ? 'Virtual' : 'Hybrid'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* Defaults Configuration Tab */}
                    <TabsContent value="defaults">
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-sm text-gray-700">Default Categories</h4>
                              <p className="text-xs text-gray-500">Select which default categories are visible for this pilot type</p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              Industry: {editData.industry || 'universal'}
                            </Badge>
                          </div>
                          
                          {/* Industry Selection */}
                          <div className="p-3 border rounded-lg bg-gray-50">
                            <Label className="text-sm mb-2 block">Pilot Industry</Label>
                            <Select
                              value={editData.industry || 'universal'}
                              onValueChange={(value) => setEditData(prev => ({
                                ...prev,
                                industry: value
                              }))}
                              disabled={!editMode}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select industry" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="universal">Universal (All)</SelectItem>
                                <SelectItem value="events">Events & Networking</SelectItem>
                                <SelectItem value="beauty">Beauty & Wellness</SelectItem>
                                <SelectItem value="lifestyle">Lifestyle</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-2">
                              This determines which default categories are shown by default
                            </p>
                          </div>
                          
                          {/* Default Categories Grid */}
                          <div className="space-y-3">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Universal Defaults</p>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                { id: 'dietary', label: 'Dietary', desc: 'Food preferences & restrictions', color: 'orange' },
                                { id: 'accessibility', label: 'Accessibility', desc: 'Accommodations & needs', color: 'blue' },
                                { id: 'interests', label: 'Interests', desc: 'Industries & hobbies', color: 'pink' },
                                { id: 'audience', label: 'Audience', desc: 'Learning preferences', color: 'violet' }
                              ].map(cat => (
                                <div key={cat.id} className={`flex items-center justify-between p-3 border rounded-lg ${
                                  editData.enabled_defaults?.includes(cat.id) !== false ? `bg-${cat.color}-50 border-${cat.color}-200` : 'bg-gray-50'
                                }`}>
                                  <div>
                                    <Label className="text-sm">{cat.label}</Label>
                                    <p className="text-xs text-gray-500">{cat.desc}</p>
                                  </div>
                                  <Switch
                                    checked={editData.enabled_defaults?.includes(cat.id) !== false}
                                    onCheckedChange={(checked) => {
                                      const current = editData.enabled_defaults || ['dietary', 'accessibility', 'interests', 'audience'];
                                      setEditData(prev => ({
                                        ...prev,
                                        enabled_defaults: checked 
                                          ? [...new Set([...current, cat.id])]
                                          : current.filter(c => c !== cat.id)
                                      }));
                                    }}
                                    disabled={!editMode}
                                  />
                                </div>
                              ))}
                            </div>
                            
                            {(editData.industry === 'events' || editData.industry === 'universal') && (
                              <>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider pt-4">Events Defaults</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    { id: 'builder_types', label: 'Builder Types', desc: 'Product/company building', color: 'emerald' },
                                    { id: 'industries', label: 'Industries', desc: 'Sectors & verticals', color: 'indigo' },
                                    { id: 'vibe_options', label: 'Vibe', desc: 'Event atmosphere', color: 'purple' }
                                  ].map(cat => (
                                    <div key={cat.id} className={`flex items-center justify-between p-3 border rounded-lg ${
                                      editData.enabled_defaults?.includes(cat.id) ? `bg-${cat.color}-50 border-${cat.color}-200` : 'bg-gray-50'
                                    }`}>
                                      <div>
                                        <Label className="text-sm">{cat.label}</Label>
                                        <p className="text-xs text-gray-500">{cat.desc}</p>
                                      </div>
                                      <Switch
                                        checked={editData.enabled_defaults?.includes(cat.id) || false}
                                        onCheckedChange={(checked) => {
                                          const current = editData.enabled_defaults || [];
                                          setEditData(prev => ({
                                            ...prev,
                                            enabled_defaults: checked 
                                              ? [...new Set([...current, cat.id])]
                                              : current.filter(c => c !== cat.id)
                                          }));
                                        }}
                                        disabled={!editMode}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                            
                            {(editData.industry === 'beauty' || editData.industry === 'universal') && (
                              <>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider pt-4">Beauty Defaults</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    { id: 'hair', label: 'Hair', desc: 'Type, length & preferences', color: 'fuchsia' },
                                    { id: 'nails', label: 'Nails', desc: 'Shape & style preferences', color: 'rose' },
                                    { id: 'face', label: 'Face', desc: 'Makeup & skin info', color: 'pink' },
                                    { id: 'lash', label: 'Lashes', desc: 'Style preferences', color: 'purple' }
                                  ].map(cat => (
                                    <div key={cat.id} className={`flex items-center justify-between p-3 border rounded-lg ${
                                      editData.enabled_defaults?.includes(cat.id) ? `bg-${cat.color}-50 border-${cat.color}-200` : 'bg-gray-50'
                                    }`}>
                                      <div>
                                        <Label className="text-sm">{cat.label}</Label>
                                        <p className="text-xs text-gray-500">{cat.desc}</p>
                                      </div>
                                      <Switch
                                        checked={editData.enabled_defaults?.includes(cat.id) || false}
                                        onCheckedChange={(checked) => {
                                          const current = editData.enabled_defaults || [];
                                          setEditData(prev => ({
                                            ...prev,
                                            enabled_defaults: checked 
                                              ? [...new Set([...current, cat.id])]
                                              : current.filter(c => c !== cat.id)
                                          }));
                                        }}
                                        disabled={!editMode}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                            
                            {(editData.industry === 'lifestyle' || editData.industry === 'universal') && (
                              <>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider pt-4">Lifestyle Defaults</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    { id: 'travel', label: 'Travel', desc: 'Preferences & style', color: 'sky' },
                                    { id: 'wellness', label: 'Wellness', desc: 'Spa & fitness preferences', color: 'emerald' }
                                  ].map(cat => (
                                    <div key={cat.id} className={`flex items-center justify-between p-3 border rounded-lg ${
                                      editData.enabled_defaults?.includes(cat.id) ? `bg-${cat.color}-50 border-${cat.color}-200` : 'bg-gray-50'
                                    }`}>
                                      <div>
                                        <Label className="text-sm">{cat.label}</Label>
                                        <p className="text-xs text-gray-500">{cat.desc}</p>
                                      </div>
                                      <Switch
                                        checked={editData.enabled_defaults?.includes(cat.id) || false}
                                        onCheckedChange={(checked) => {
                                          const current = editData.enabled_defaults || [];
                                          setEditData(prev => ({
                                            ...prev,
                                            enabled_defaults: checked 
                                              ? [...new Set([...current, cat.id])]
                                              : current.filter(c => c !== cat.id)
                                          }));
                                        }}
                                        disabled={!editMode}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          
                          <div className="p-3 border rounded-lg bg-amber-50 border-amber-200 mt-4">
                            <p className="text-xs text-amber-800">
                              <strong>Note:</strong> Only published defaults from the Admin Defaults page will be shown. Draft defaults are hidden from pilots and participants.
                            </p>
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* Host Controls Tab */}
                    <TabsContent value="display">
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          <h4 className="font-medium text-sm text-gray-700">Host Dashboard Controls</h4>
                          <p className="text-xs text-gray-500">Configure what hosts can see and manage in their dashboard</p>
                          
                          <div className="grid grid-cols-2 gap-3">
                            {/* Show Dietary */}
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <Label className="text-sm">Show Dietary Info</Label>
                                <p className="text-xs text-gray-500">Display dietary needs to hosts</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.host_display_controls?.show_dietary ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    host_display_controls: { ...(prev.host_display_controls || {}), show_dietary: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.host_display_controls?.show_dietary ? "default" : "outline"}>
                                  {editData.host_display_controls?.show_dietary ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Show Accessibility */}
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <Label className="text-sm">Show Accessibility</Label>
                                <p className="text-xs text-gray-500">Display accessibility needs</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.host_display_controls?.show_accessibility ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    host_display_controls: { ...(prev.host_display_controls || {}), show_accessibility: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.host_display_controls?.show_accessibility ? "default" : "outline"}>
                                  {editData.host_display_controls?.show_accessibility ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Show Attendee List */}
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <Label className="text-sm">Show Attendee List</Label>
                                <p className="text-xs text-gray-500">Let hosts see who RSVPd</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.host_display_controls?.show_attendee_list ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    host_display_controls: { ...(prev.host_display_controls || {}), show_attendee_list: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.host_display_controls?.show_attendee_list ? "default" : "outline"}>
                                  {editData.host_display_controls?.show_attendee_list ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Allow Check-ins */}
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <Label className="text-sm">Allow Check-ins</Label>
                                <p className="text-xs text-gray-500">Enable check-in functionality</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.host_display_controls?.allow_checkins ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    host_display_controls: { ...(prev.host_display_controls || {}), allow_checkins: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.host_display_controls?.allow_checkins ? "default" : "outline"}>
                                  {editData.host_display_controls?.allow_checkins ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Show Analytics */}
                            <div className="flex items-center justify-between p-3 border rounded-lg col-span-2">
                              <div>
                                <Label className="text-sm">Show Analytics</Label>
                                <p className="text-xs text-gray-500">Display aggregate insights to hosts</p>
                              </div>
                              {editMode ? (
                                <Switch
                                  checked={editData.host_display_controls?.show_analytics ?? true}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    host_display_controls: { ...(prev.host_display_controls || {}), show_analytics: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={editData.host_display_controls?.show_analytics ? "default" : "outline"}>
                                  {editData.host_display_controls?.show_analytics ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* Welcome Page Tab */}
                    <TabsContent value="welcome">
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          <h4 className="font-medium text-sm text-gray-700">Welcome Page Configuration</h4>
                          <p className="text-xs text-gray-500">Customize what users see when they sign up or RSVP</p>
                          
                          {/* Welcome Message */}
                          <div className="border rounded-lg p-4">
                            <Label className="text-sm mb-2 block">Welcome Message</Label>
                            {editMode ? (
                              <Textarea
                                value={editData.welcome_config?.message || ""}
                                onChange={(e) => setEditData(prev => ({
                                  ...prev,
                                  welcome_config: { ...(prev.welcome_config || {}), message: e.target.value }
                                }))}
                                placeholder="Welcome! We're excited to have you join us..."
                                rows={3}
                              />
                            ) : (
                              <p className="text-sm text-gray-600">
                                {editData.welcome_config?.message || "No welcome message set"}
                              </p>
                            )}
                          </div>
                          
                          {/* Confirmation Message */}
                          <div className="border rounded-lg p-4">
                            <Label className="text-sm mb-2 block">Confirmation Message</Label>
                            {editMode ? (
                              <Textarea
                                value={editData.welcome_config?.confirmation_message || ""}
                                onChange={(e) => setEditData(prev => ({
                                  ...prev,
                                  welcome_config: { ...(prev.welcome_config || {}), confirmation_message: e.target.value }
                                }))}
                                placeholder="You're confirmed! Check your email for details..."
                                rows={2}
                              />
                            ) : (
                              <p className="text-sm text-gray-600">
                                {editData.welcome_config?.confirmation_message || "Default confirmation message"}
                              </p>
                            )}
                          </div>
                          
                          {/* Show Next Steps */}
                          <div className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <Label className="text-sm">Show Next Steps</Label>
                              <p className="text-xs text-gray-500">Display action items after RSVP</p>
                            </div>
                            {editMode ? (
                              <Switch
                                checked={editData.welcome_config?.show_next_steps ?? true}
                                onCheckedChange={(checked) => setEditData(prev => ({
                                  ...prev,
                                  welcome_config: { ...(prev.welcome_config || {}), show_next_steps: checked }
                                }))}
                              />
                            ) : (
                              <Badge variant={editData.welcome_config?.show_next_steps ? "default" : "outline"}>
                                {editData.welcome_config?.show_next_steps ? "Yes" : "No"}
                              </Badge>
                            )}
                          </div>
                          
                          {/* CTA Configuration */}
                          <div className="border rounded-lg p-4">
                            <Label className="text-sm mb-2 block">Call-to-Action Button</Label>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-gray-500">Button Text</Label>
                                {editMode ? (
                                  <Input
                                    value={editData.welcome_config?.cta_text || ""}
                                    onChange={(e) => setEditData(prev => ({
                                      ...prev,
                                      welcome_config: { ...(prev.welcome_config || {}), cta_text: e.target.value }
                                    }))}
                                    placeholder="Add to Calendar"
                                    className="mt-1"
                                  />
                                ) : (
                                  <p className="text-sm font-medium mt-1">
                                    {editData.welcome_config?.cta_text || "Default CTA"}
                                  </p>
                                )}
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Button Link</Label>
                                {editMode ? (
                                  <Input
                                    value={editData.welcome_config?.cta_link || ""}
                                    onChange={(e) => setEditData(prev => ({
                                      ...prev,
                                      welcome_config: { ...(prev.welcome_config || {}), cta_link: e.target.value }
                                    }))}
                                    placeholder="https://..."
                                    className="mt-1"
                                  />
                                ) : (
                                  <p className="text-sm text-gray-600 mt-1 truncate">
                                    {editData.welcome_config?.cta_link || "No link set"}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    
                    {/* Event Defaults Tab */}
                    <TabsContent value="defaults">
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(editData.event_defaults || {}).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                              <Label className="text-sm">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Label>
                              {editMode ? (
                                <Switch
                                  checked={Boolean(value)}
                                  onCheckedChange={(checked) => setEditData(prev => ({
                                    ...prev,
                                    event_defaults: { ...prev.event_defaults, [key]: checked }
                                  }))}
                                />
                              ) : (
                                <Badge variant={value ? "default" : "outline"}>
                                  {value ? "Yes" : "No"}
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        <div className="border rounded-lg p-4">
                          <Label className="text-sm mb-2 block">Default Event Type</Label>
                          {editMode ? (
                            <Select
                              value={editData.default_event_type || "hybrid"}
                              onValueChange={(v) => setEditData(prev => ({ ...prev, default_event_type: v }))}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="irl">IRL (In-Person)</SelectItem>
                                <SelectItem value="url">URL (Virtual)</SelectItem>
                                <SelectItem value="hybrid">Hybrid</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline">
                              {editData.default_event_type === 'irl' ? 'In-Person' : 
                               editData.default_event_type === 'url' ? 'Virtual' : 'Hybrid'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Branding Tab */}
                    <TabsContent value="branding">
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm">Profile Name</Label>
                            {editMode ? (
                              <Input
                                value={editData.name || ""}
                                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                                className="mt-1"
                              />
                            ) : (
                              <p className="mt-1 font-medium">{editData.name}</p>
                            )}
                          </div>
                          
                          <div>
                            <Label className="text-sm">Brand Color</Label>
                            {editMode ? (
                              <div className="flex gap-2 mt-1">
                                <Input
                                  type="color"
                                  value={editData.color || "#8b5cf6"}
                                  onChange={(e) => setEditData(prev => ({ ...prev, color: e.target.value }))}
                                  className="w-12 h-10 p-1"
                                />
                                <Input
                                  value={editData.color || "#8b5cf6"}
                                  onChange={(e) => setEditData(prev => ({ ...prev, color: e.target.value }))}
                                />
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 mt-1">
                                <div 
                                  className="w-8 h-8 rounded"
                                  style={{ backgroundColor: editData.color || "#8b5cf6" }}
                                />
                                <span className="text-sm text-gray-600">{editData.color}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-sm">Description</Label>
                          {editMode ? (
                            <Textarea
                              value={editData.description || ""}
                              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                              className="mt-1"
                              rows={3}
                            />
                          ) : (
                            <p className="mt-1 text-gray-600">{editData.description || "No description"}</p>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <Label className="text-sm">Public Profile</Label>
                            <p className="text-xs text-gray-500">Allow others to view this profile</p>
                          </div>
                          {editMode ? (
                            <Switch
                              checked={editData.is_public || false}
                              onCheckedChange={(checked) => setEditData(prev => ({ ...prev, is_public: checked }))}
                            />
                          ) : (
                            <Badge variant={editData.is_public ? "default" : "outline"}>
                              {editData.is_public ? "Public" : "Private"}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <Label className="text-sm">Status</Label>
                            <p className="text-xs text-gray-500">Active pilots are visible to hosts</p>
                          </div>
                          {editMode ? (
                            <Select
                              value={editData.status || "draft"}
                              onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant={editData.status === 'active' ? "default" : "outline"}>
                              {editData.status || 'draft'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Onboarding Tab */}
                    <TabsContent value="onboarding">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <Label className="text-sm">Enable Instructor Onboarding</Label>
                            <p className="text-xs text-gray-500">Allow new instructors to join this pilot</p>
                          </div>
                          {editMode ? (
                            <Switch
                              checked={editData.instructor_onboarding?.enabled || false}
                              onCheckedChange={(checked) => setEditData(prev => ({
                                ...prev,
                                instructor_onboarding: { ...(prev.instructor_onboarding || {}), enabled: checked }
                              }))}
                            />
                          ) : (
                            <Badge variant={editData.instructor_onboarding?.enabled ? "default" : "outline"}>
                              {editData.instructor_onboarding?.enabled ? "Enabled" : "Disabled"}
                            </Badge>
                          )}
                        </div>
                        
                        {(editData.instructor_onboarding?.enabled || editMode) && (
                          <>
                            <div>
                              <Label className="text-sm">Welcome Message</Label>
                              {editMode ? (
                                <Textarea
                                  value={editData.instructor_onboarding?.welcome_message || ""}
                                  onChange={(e) => setEditData(prev => ({
                                    ...prev,
                                    instructor_onboarding: { 
                                      ...(prev.instructor_onboarding || {}), 
                                      welcome_message: e.target.value 
                                    }
                                  }))}
                                  className="mt-1"
                                  rows={3}
                                  placeholder="Welcome message for new instructors..."
                                />
                              ) : (
                                <p className="mt-1 text-gray-600 text-sm">
                                  {editData.instructor_onboarding?.welcome_message || "No welcome message set"}
                                </p>
                              )}
                            </div>
                            
                            <div>
                              <Label className="text-sm mb-2 block">Intake Fields</Label>
                              <div className="flex flex-wrap gap-2">
                                {(editData.instructor_onboarding?.intake_fields || ['name', 'email', 'bio']).map(field => (
                                  <Badge key={field} variant="outline">{field}</Badge>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center min-h-[500px]">
                <CardContent className="text-center">
                  <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 mb-2">Select a Pilot to Edit</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Choose a pilot from the list to view and edit its configuration
                  </p>
                  <Button variant="outline" onClick={() => navigate("/admin/instructor-pilots")}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Pilot
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      
      {/* Add Field Dialog */}
      <Dialog open={newFieldDialog.open} onOpenChange={(open) => setNewFieldDialog({ open, category: newFieldDialog.category })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Field</DialogTitle>
            <DialogDescription>
              Add a new option to {newFieldDialog.category?.replace(/_/g, ' ')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Field Name</Label>
              <Input
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="e.g., AI/ML"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNewFieldDialog({ open: false, category: null })}>
                Cancel
              </Button>
              <Button onClick={() => addNewField(newFieldDialog.category)}>
                Add Field
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Apply Dialog */}
      <Dialog open={showBulkApplyDialog} onOpenChange={setShowBulkApplyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Apply Settings</DialogTitle>
            <DialogDescription>
              Choose which settings to apply to {selectedProfiles.size} selected pilots
            </DialogDescription>
          </DialogHeader>
          
          <BulkApplyForm 
            onApply={handleBulkApply}
            onCancel={() => setShowBulkApplyDialog(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Bulk Apply Form Component
function BulkApplyForm({ onApply, onCancel, saving }) {
  const [fieldsToApply, setFieldsToApply] = useState({
    event_defaults: false,
    intake_settings: false,
    host_display_controls: false,
    welcome_config: false,
    audience_types: false,
    interests: false,
    builder_types: false,
    industries: false,
    accessibility_options: false,
    dietary_options: false
  });
  
  const toggleField = (field) => {
    setFieldsToApply(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  const selectedCount = Object.values(fieldsToApply).filter(Boolean).length;
  
  const fieldGroups = [
    {
      title: "Configuration",
      fields: [
        { key: "event_defaults", label: "Event Defaults (capacity, pre-invite, etc.)" },
        { key: "intake_settings", label: "Intake Settings (what to collect)" },
        { key: "host_display_controls", label: "Host Dashboard Controls" },
        { key: "welcome_config", label: "Welcome Page Configuration" }
      ]
    },
    {
      title: "Field Options",
      fields: [
        { key: "audience_types", label: "Audience Types" },
        { key: "interests", label: "Interests/Topics" },
        { key: "builder_types", label: "Builder Types" },
        { key: "industries", label: "Industries" },
        { key: "accessibility_options", label: "Accessibility Options" },
        { key: "dietary_options", label: "Dietary Options" }
      ]
    }
  ];
  
  return (
    <div className="space-y-4">
      {fieldGroups.map(group => (
        <div key={group.title}>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">{group.title}</Label>
          <div className="space-y-2">
            {group.fields.map(field => (
              <div
                key={field.key}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  fieldsToApply[field.key]
                    ? "border-violet-500 bg-violet-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => toggleField(field.key)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    fieldsToApply[field.key]
                      ? "bg-violet-600 border-violet-600"
                      : "border-gray-300"
                  }`}>
                    {fieldsToApply[field.key] && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-sm">{field.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      
      <div className="flex items-center justify-between pt-4 border-t">
        <span className="text-sm text-gray-500">
          {selectedCount} setting{selectedCount !== 1 ? 's' : ''} selected
        </span>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button 
            onClick={() => onApply(fieldsToApply)} 
            disabled={selectedCount === 0 || saving}
            className="bg-violet-600 hover:bg-violet-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>Apply to All Selected</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
